/**
 * Phase 3 company ops settings. Placeholders only — Inbox mint and the
 * quote card keep reading the existing rate tables and flight-number fields.
 * Zero money means "unset", never a baked product-spec example.
 */
import { companyFlightNumberScheme, type FlightNumberScheme } from "./flightNumber";
import { hydrateFuelUnit, type FuelUnit } from "./fuelUnits";
import type {
  CompanyProfile,
  OpsAircraftHourlyCost,
  OpsAirportOps,
  OpsConstraintEnforcement,
  OpsCostRates,
  OpsCrewPayRates,
  OpsCurfewWindow,
  OpsDeclineReason,
  OpsDeskSettings,
  OpsFerryRules,
  OpsTailHourlyCost,
} from "./types";

export const OPS_RFQ_HORIZON_DAYS = { min: 1, max: 7, default: 2 } as const;

export const DEFAULT_OPS_COST_RATES: OpsCostRates = {
  fuelPerUnit: 0,
  landing: 0,
  handling: 0,
  catering: 0,
  mxInsurancePerHour: 0,
  deice: 0,
};

export const DEFAULT_OPS_CREW_PAY: OpsCrewPayRates = {
  payPerHour: 0,
  overnightHotel: 0,
  overnightMeal: 0,
  overnightGround: 0,
};

export const DEFAULT_DECLINE_REASONS: readonly OpsDeclineReason[] = [
  { id: "schedule", label: "Schedule", enabled: true },
  { id: "crew", label: "Crew / duty", enabled: true },
  { id: "aircraft", label: "Aircraft / mx", enabled: true },
  { id: "weather", label: "Weather", enabled: true },
  { id: "price", label: "Price", enabled: true },
  { id: "other", label: "Other", enabled: true },
];

const DECLINE_CAP = 24;
const AIRPORT_OPS_CAP = 200;
const AIRCRAFT_COST_CAP = 48;
const TAIL_COST_CAP = 80;

export const DEFAULT_CURFEW_WINDOW: OpsCurfewWindow = {
  quietStartMin: 0,
  quietEndMin: 0,
  unrestricted: false,
};

export const DEFAULT_FERRY_RULES: OpsFerryRules = {
  allowConvert: true,
  backhaulFactor: 0,
  fillFactor: 0,
};

export function defaultOpsDeskSettings(): OpsDeskSettings {
  return {
    costRates: { ...DEFAULT_OPS_COST_RATES },
    crewPay: { ...DEFAULT_OPS_CREW_PAY },
    declineReasons: DEFAULT_DECLINE_REASONS.map((row) => ({ ...row })),
    rfqHorizonDays: OPS_RFQ_HORIZON_DAYS.default,
    dualPilotDefault: true,
    singlePilotEnabled: false,
    crewMinEnforcement: "flag",
    curfewUseCompanyDefault: false,
    defaultCurfew: { ...DEFAULT_CURFEW_WINDOW },
    curfewEnforcement: "flag",
    customsOnCountryChange: false,
    customsEnforcement: "flag",
    standardPaxLb: 0,
    weightEnforcement: "flag",
    airports: [],
    ferryRules: { ...DEFAULT_FERRY_RULES },
    deiceTempThresholdC: 0,
    deiceEnforcement: "flag",
    deiceDelayMin: 0,
    convectiveHoldEnabled: false,
    convectiveHoldEnforcement: "flag",
    aircraftCosts: [],
    tailCostOverrides: [],
  };
}

function money(raw: unknown): number {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : NaN;
  if (!Number.isFinite(n) || n < 0) return 0;
  return Math.round(Math.min(n, 1_000_000) * 100) / 100;
}

function hydrateCostRates(raw: unknown): OpsCostRates {
  const src = raw && typeof raw === "object" ? (raw as Partial<OpsCostRates>) : {};
  return {
    fuelPerUnit: money(src.fuelPerUnit),
    landing: money(src.landing),
    handling: money(src.handling),
    catering: money(src.catering),
    mxInsurancePerHour: money(src.mxInsurancePerHour),
    deice: money(src.deice),
  };
}

function hydrateCrewPay(raw: unknown): OpsCrewPayRates {
  const src = raw && typeof raw === "object" ? (raw as Partial<OpsCrewPayRates>) : {};
  return {
    payPerHour: money(src.payPerHour),
    overnightHotel: money(src.overnightHotel),
    overnightMeal: money(src.overnightMeal),
    overnightGround: money(src.overnightGround),
  };
}

export function normalizeDeclineReasonId(raw: unknown): string {
  return String(raw ?? "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 32);
}

function hydrateDeclineReasons(raw: unknown): OpsDeclineReason[] {
  if (!Array.isArray(raw)) return DEFAULT_DECLINE_REASONS.map((row) => ({ ...row }));
  const out: OpsDeclineReason[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const src = row as Partial<OpsDeclineReason>;
    const id = normalizeDeclineReasonId(src.id);
    const label = String(src.label ?? "").trim().slice(0, 48);
    if (!id || !label || seen.has(id)) continue;
    seen.add(id);
    out.push({ id, label, enabled: src.enabled !== false });
    if (out.length >= DECLINE_CAP) break;
  }
  return out;
}

export function hydrateRfqHorizonDays(raw: unknown): number {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return OPS_RFQ_HORIZON_DAYS.default;
  return Math.min(OPS_RFQ_HORIZON_DAYS.max, Math.max(OPS_RFQ_HORIZON_DAYS.min, Math.round(n)));
}

function enforcement(raw: unknown): OpsConstraintEnforcement {
  return raw === "block" ? "block" : "flag";
}

function minuteOfDay(raw: unknown): number {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return 0;
  return Math.min(24 * 60 - 1, Math.max(0, Math.round(n)));
}

export function hydrateCurfewWindow(raw: unknown): OpsCurfewWindow {
  const src = raw && typeof raw === "object" ? (raw as Partial<OpsCurfewWindow>) : {};
  return {
    quietStartMin: minuteOfDay(src.quietStartMin),
    quietEndMin: minuteOfDay(src.quietEndMin),
    unrestricted: src.unrestricted === true,
  };
}

function hydrateAirportOps(raw: unknown): OpsAirportOps[] {
  if (!Array.isArray(raw)) return [];
  const out: OpsAirportOps[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const src = row as Partial<OpsAirportOps>;
    const icao = String(src.icao ?? "")
      .trim()
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, "")
      .slice(0, 4);
    if (icao.length < 3 || seen.has(icao)) continue;
    seen.add(icao);
    const notes = String(src.notes ?? "").trim().slice(0, 240);
    const next: OpsAirportOps = { icao };
    if (src.curfew && typeof src.curfew === "object") next.curfew = hydrateCurfewWindow(src.curfew);
    if (src.customsAvailable === true || src.customsAvailable === false) next.customsAvailable = src.customsAvailable;
    if (notes) next.notes = notes;
    if (!next.curfew && next.customsAvailable === undefined && !next.notes) continue;
    out.push(next);
    if (out.length >= AIRPORT_OPS_CAP) break;
  }
  return out;
}

function factor(raw: unknown): number {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : NaN;
  if (!Number.isFinite(n) || n <= 0 || n > 1) return 0;
  return Math.round(n * 1000) / 1000;
}

export function hydrateFerryRules(raw: unknown): OpsFerryRules {
  const src = raw && typeof raw === "object" ? (raw as Partial<OpsFerryRules>) : {};
  return {
    allowConvert: src.allowConvert !== false,
    backhaulFactor: factor(src.backhaulFactor),
    fillFactor: factor(src.fillFactor),
  };
}

function icaoTypeToken(raw: unknown): string {
  return String(raw ?? "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, 4);
}

function classLabel(raw: unknown): string {
  return String(raw ?? "")
    .trim()
    .replace(/\s+/g, " ")
    .slice(0, 32);
}

function wholeNumber(raw: unknown, max: number): number {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : NaN;
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(max, Math.round(n));
}

function tailId(raw: unknown): string {
  return String(raw ?? "").trim().slice(0, 64);
}

/**
 * Variable DOC components. A legacy all-in `hourlyOperatingCost` is ignored:
 * that figure mixed trip fees into the hour and must not become fuel planning.
 */
function docComponents(src: Partial<OpsAircraftHourlyCost | OpsTailHourlyCost> | null | undefined): {
  fuelPerHour: number;
  mxPerHour: number;
  enginePerHour: number;
  insurancePerHour: number;
} {
  return {
    fuelPerHour: money(src?.fuelPerHour),
    mxPerHour: money(src?.mxPerHour),
    enginePerHour: money(src?.enginePerHour),
    insurancePerHour: money(src?.insurancePerHour),
  };
}

export function hydrateAircraftHourlyCosts(raw: unknown): OpsAircraftHourlyCost[] {
  if (!Array.isArray(raw)) return [];
  const out: OpsAircraftHourlyCost[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const src = row as Partial<OpsAircraftHourlyCost>;
    const icaoType = icaoTypeToken(src.icaoType);
    if (icaoType.length < 2 || seen.has(icaoType)) continue;
    seen.add(icaoType);
    out.push({
      icaoType,
      classLabel: classLabel(src.classLabel),
      ...docComponents(src),
      cruiseKtas: wholeNumber(src.cruiseKtas, 800),
      nbaaRangeNm: wholeNumber(src.nbaaRangeNm, 20000),
    });
    if (out.length >= AIRCRAFT_COST_CAP) break;
  }
  return out;
}

export function hydrateTailHourlyCosts(raw: unknown): OpsTailHourlyCost[] {
  if (!Array.isArray(raw)) return [];
  const out: OpsTailHourlyCost[] = [];
  const seen = new Set<string>();
  for (const row of raw) {
    if (!row || typeof row !== "object") continue;
    const src = row as Partial<OpsTailHourlyCost>;
    const aircraftId = tailId(src.aircraftId);
    if (!aircraftId || seen.has(aircraftId)) continue;
    const components = docComponents(src);
    const cruiseKtas = wholeNumber(src.cruiseKtas, 800);
    const nbaaRangeNm = wholeNumber(src.nbaaRangeNm, 20000);
    const anyMoney =
      components.fuelPerHour > 0 ||
      components.mxPerHour > 0 ||
      components.enginePerHour > 0 ||
      components.insurancePerHour > 0;
    if (!anyMoney && cruiseKtas <= 0 && nbaaRangeNm <= 0) continue;
    seen.add(aircraftId);
    out.push({ aircraftId, ...components, cruiseKtas, nbaaRangeNm });
    if (out.length >= TAIL_COST_CAP) break;
  }
  return out;
}

export function upsertAircraftHourlyCost(
  current: OpsDeskSettings | null | undefined,
  row: Partial<OpsAircraftHourlyCost> & Pick<OpsAircraftHourlyCost, "icaoType">,
): OpsDeskSettings {
  const base = hydrateOpsDeskSettings(current);
  const icaoType = icaoTypeToken(row.icaoType);
  if (icaoType.length < 2) return base;
  const next = hydrateAircraftHourlyCosts([
    ...base.aircraftCosts.filter((item) => item.icaoType !== icaoType),
    {
      icaoType,
      classLabel: row.classLabel ?? "",
      ...docComponents(row),
      cruiseKtas: row.cruiseKtas ?? 0,
      nbaaRangeNm: row.nbaaRangeNm ?? 0,
    },
  ]);
  return patchOpsDeskSettings(base, { aircraftCosts: next });
}

export function removeAircraftHourlyCost(
  current: OpsDeskSettings | null | undefined,
  icaoType: string,
): OpsDeskSettings {
  const base = hydrateOpsDeskSettings(current);
  const token = icaoTypeToken(icaoType);
  return patchOpsDeskSettings(base, {
    aircraftCosts: base.aircraftCosts.filter((row) => row.icaoType !== token),
  });
}

export function upsertTailHourlyCost(
  current: OpsDeskSettings | null | undefined,
  row: Partial<OpsTailHourlyCost> & Pick<OpsTailHourlyCost, "aircraftId">,
): OpsDeskSettings {
  const base = hydrateOpsDeskSettings(current);
  const aircraftId = tailId(row.aircraftId);
  if (!aircraftId) return base;
  const next = hydrateTailHourlyCosts([
    ...base.tailCostOverrides.filter((item) => item.aircraftId !== aircraftId),
    {
      aircraftId,
      ...docComponents(row),
      cruiseKtas: row.cruiseKtas ?? 0,
      nbaaRangeNm: row.nbaaRangeNm ?? 0,
    },
  ]);
  return patchOpsDeskSettings(base, { tailCostOverrides: next });
}

function hydrateStandardPaxLb(raw: unknown): number {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : NaN;
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(1000, Math.round(n));
}

function hydrateDeiceTempC(raw: unknown): number {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : NaN;
  if (!Number.isFinite(n)) return 0;
  return Math.min(20, Math.max(-40, Math.round(n)));
}

function hydrateDelayMin(raw: unknown): number {
  const n = typeof raw === "number" ? raw : typeof raw === "string" && raw.trim() ? Number(raw) : NaN;
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(240, Math.round(n));
}

/** Missing object → defaults. A present decline list, including `[]`, is kept. */
export function hydrateOpsDeskSettings(raw?: unknown): OpsDeskSettings {
  const src = raw && typeof raw === "object" ? (raw as Partial<OpsDeskSettings>) : {};
  return {
    costRates: hydrateCostRates(src.costRates),
    crewPay: hydrateCrewPay(src.crewPay),
    declineReasons: hydrateDeclineReasons(src.declineReasons),
    rfqHorizonDays: hydrateRfqHorizonDays(src.rfqHorizonDays),
    dualPilotDefault: src.dualPilotDefault !== false,
    singlePilotEnabled: src.singlePilotEnabled === true,
    crewMinEnforcement: enforcement(src.crewMinEnforcement),
    curfewUseCompanyDefault: src.curfewUseCompanyDefault === true,
    defaultCurfew: hydrateCurfewWindow(src.defaultCurfew),
    curfewEnforcement: enforcement(src.curfewEnforcement),
    customsOnCountryChange: src.customsOnCountryChange === true,
    customsEnforcement: enforcement(src.customsEnforcement),
    standardPaxLb: hydrateStandardPaxLb(src.standardPaxLb),
    weightEnforcement: enforcement(src.weightEnforcement),
    airports: hydrateAirportOps(src.airports),
    ferryRules: hydrateFerryRules(src.ferryRules),
    deiceTempThresholdC: hydrateDeiceTempC(src.deiceTempThresholdC),
    deiceEnforcement: enforcement(src.deiceEnforcement),
    deiceDelayMin: hydrateDelayMin(src.deiceDelayMin),
    convectiveHoldEnabled: src.convectiveHoldEnabled === true,
    convectiveHoldEnforcement: enforcement(src.convectiveHoldEnforcement),
    aircraftCosts: hydrateAircraftHourlyCosts(src.aircraftCosts),
    tailCostOverrides: hydrateTailHourlyCosts(src.tailCostOverrides),
  };
}

export type OpsDeskPatch = {
  costRates?: Partial<OpsCostRates>;
  crewPay?: Partial<OpsCrewPayRates>;
  declineReasons?: OpsDeskSettings["declineReasons"];
  rfqHorizonDays?: number;
  dualPilotDefault?: boolean;
  singlePilotEnabled?: boolean;
  crewMinEnforcement?: OpsConstraintEnforcement;
  curfewUseCompanyDefault?: boolean;
  defaultCurfew?: Partial<OpsCurfewWindow>;
  curfewEnforcement?: OpsConstraintEnforcement;
  customsOnCountryChange?: boolean;
  customsEnforcement?: OpsConstraintEnforcement;
  standardPaxLb?: number;
  weightEnforcement?: OpsConstraintEnforcement;
  airports?: OpsAirportOps[];
  ferryRules?: Partial<OpsFerryRules>;
  deiceTempThresholdC?: number;
  deiceEnforcement?: OpsConstraintEnforcement;
  deiceDelayMin?: number;
  convectiveHoldEnabled?: boolean;
  convectiveHoldEnforcement?: OpsConstraintEnforcement;
  aircraftCosts?: OpsAircraftHourlyCost[];
  tailCostOverrides?: OpsTailHourlyCost[];
};

export function patchOpsDeskSettings(
  current: OpsDeskSettings | null | undefined,
  patch: OpsDeskPatch | null | undefined,
): OpsDeskSettings {
  const base = hydrateOpsDeskSettings(current);
  const raw = patch && typeof patch === "object" ? patch : {};
  return hydrateOpsDeskSettings({
    ...base,
    ...raw,
    costRates: { ...base.costRates, ...raw.costRates },
    crewPay: { ...base.crewPay, ...raw.crewPay },
    declineReasons: raw.declineReasons ?? base.declineReasons,
    defaultCurfew: { ...base.defaultCurfew, ...raw.defaultCurfew },
    airports: raw.airports ?? base.airports,
    ferryRules: { ...base.ferryRules, ...raw.ferryRules },
    aircraftCosts: raw.aircraftCosts ?? base.aircraftCosts,
    tailCostOverrides: raw.tailCostOverrides ?? base.tailCostOverrides,
  });
}

export type OpsFlightNumberSlice = FlightNumberScheme & {
  nextFlightSeq: number;
  nextFerrySeq: number;
};

/**
 * One read of the settings the later cost / crew / RFQ slices should share.
 * Flight numbers and the company fuel unit stay on the fields Settings already owns.
 */
export type OpsSettingsSlice = OpsDeskSettings & {
  defaultFuelUnit: FuelUnit;
  flightNumbers: OpsFlightNumberSlice;
};

export function opsSettingsSlice(company?: Partial<CompanyProfile> | null): OpsSettingsSlice {
  const ops = hydrateOpsDeskSettings(company?.opsDesk);
  const scheme = companyFlightNumberScheme(company);
  const nextFlight = typeof company?.nextFlightSeq === "number" && Number.isFinite(company.nextFlightSeq)
    ? Math.max(1, Math.floor(company.nextFlightSeq))
    : scheme.startSeq;
  const nextFerry = typeof company?.nextFerrySeq === "number" && Number.isFinite(company.nextFerrySeq)
    ? Math.max(1, Math.floor(company.nextFerrySeq))
    : scheme.ferryStartSeq;
  return {
    ...ops,
    declineReasons: ops.declineReasons.map((row) => ({ ...row })),
    defaultFuelUnit: hydrateFuelUnit(company?.defaultFuelUnit),
    flightNumbers: {
      ...scheme,
      nextFlightSeq: nextFlight,
      nextFerrySeq: nextFerry,
    },
  };
}

/** Effective complement. Single-pilot requires the company switch and the tail flag. */
export function resolveCrewComplement(
  settings: Pick<OpsDeskSettings, "dualPilotDefault" | "singlePilotEnabled">,
  tailSinglePilot?: boolean | null,
): "dual" | "single" {
  if (settings.singlePilotEnabled && tailSinglePilot === true) return "single";
  if (settings.dualPilotDefault) return "dual";
  return settings.singlePilotEnabled ? "single" : "dual";
}
